import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, Plus, Search, MapPin, Trophy, X, ChevronRight, Loader } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPORTS = ["tennis", "pickleball", "golf", "table-tennis", "ski", "padel", "squash", "badminton", "climbing"];
const SPORT_LABELS = { tennis:"Tennis", pickleball:"Pickleball", golf:"Golf", "table-tennis":"Table Tennis", ski:"Skiing", padel:"Padel", squash:"Squash", badminton:"Badminton", climbing:"Climbing" };

function ClubCard({ club, isOwner, isMember }) {
  return (
    <div className="bg-[#1A1F2E] border border-gray-200 rounded-xl p-5 hover:border-brand-green/25 transition-colors group dark-section" data-testid="club-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold text-white text-base">{club.name}</h3>
            {isOwner && <span className="text-xs px-2 py-0.5 rounded-full bg-lime/15 text-brand-green border border-brand-green/30">Owner</span>}
            {!isOwner && isMember && <span className="text-xs px-2 py-0.5 rounded-full bg-sky-400/15 text-sky-400 border border-sky-400/30">Member</span>}
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="capitalize">{SPORT_LABELS[club.sport] || club.sport}</span>
            <span className="flex items-center gap-1"><MapPin size={11} aria-hidden="true" />{club.city}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-heading font-black text-brand-green">{(club.members?.length || 0) + 1}</div>
          <div className="text-xs text-gray-400">members</div>
        </div>
      </div>
      {club.description && <p className="text-xs text-gray-500 mb-4">{club.description}</p>}
      <div className="flex gap-2">
        <Link to={`/clubs/${club.id}`} className="flex-1 text-center py-2 text-xs font-medium border border-gray-200 text-gray-600 rounded-lg hover:border-brand-green/40 transition-colors" data-testid="view-club-btn">
          View Club
        </Link>
        {(isOwner || isMember) && (
          <Link to={`/clubs/${club.id}/manage`} className="flex-1 text-center py-2 text-xs font-bold bg-brand-orange text-white rounded-lg hover:bg-brand-orange/90 transition-colors" data-testid="manage-club-btn">
            Manage
          </Link>
        )}
      </div>
    </div>
  );
}

function CreateClubModal({ onClose, onCreate }) {
  const [form, setForm] = useState({ name: "", sport: "tennis", city: "", description: "" });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/clubs`, form, { withCredentials: true });
      onCreate(data);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" role="dialog" aria-modal="true" aria-labelledby="create-club-title">
      <div className="bg-[#1A1F2E] border border-gray-200 rounded-xl p-6 w-full max-w-md relative dark-section">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white" aria-label="Close" data-testid="close-create-club">
          <X size={18} />
        </button>
        <h2 id="create-club-title" className="text-lg font-bold text-brand-navy mb-4">Create a Club</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5" htmlFor="club-name">Club Name</label>
            <input id="club-name" type="text" required value={form.name} onChange={(e) => setForm(p => ({...p, name: e.target.value}))}
              placeholder="Fort Worth Tennis Club" className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50" data-testid="club-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5" htmlFor="club-sport">Sport</label>
              <select id="club-sport" value={form.sport} onChange={(e) => setForm(p => ({...p, sport: e.target.value}))}
                className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-green/50" data-testid="club-sport-select">
                {SPORTS.map(s => <option key={s} value={s}>{SPORT_LABELS[s]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5" htmlFor="club-city">City</label>
              <input id="club-city" type="text" required value={form.city} onChange={(e) => setForm(p => ({...p, city: e.target.value}))}
                placeholder="Fort Worth, TX" className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50" data-testid="club-city-input" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5" htmlFor="club-description">Description</label>
            <textarea id="club-description" rows={3} value={form.description} onChange={(e) => setForm(p => ({...p, description: e.target.value}))}
              placeholder="Weekly drill sessions, all levels welcome..." className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 resize-none" data-testid="club-description-input" />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 text-sm border border-gray-200 text-gray-500 rounded-lg hover:border-slate-500 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2 text-sm font-bold bg-brand-orange text-white rounded-lg hover:bg-brand-orange/90 transition-colors disabled:opacity-50" data-testid="create-club-submit">
              {saving ? "Creating..." : "Create Club"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ClubsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();
  const [myClubs, setMyClubs] = useState([]);
  const [allClubs, setAllClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState("");
  const [sportFilter, setSportFilter] = useState("");
  const [tab, setTab] = useState("mine");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mine, all] = await Promise.all([
        user ? axios.get(`${API}/clubs/mine`, { withCredentials: true }) : Promise.resolve({ data: [] }),
        axios.get(`${API}/clubs`),
      ]);
      setMyClubs(mine.data);
      setAllClubs(all.data);
    } catch { } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = (club) => {
    setShowCreate(false);
    showToast(`${club.name} created!`);
    navigate(`/clubs/${club.id}/manage`);
  };

  const myIds = new Set(myClubs.map(c => c.id));
  const discover = allClubs.filter(c => {
    if (myIds.has(c.id)) return false;
    if (sportFilter && c.sport !== sportFilter) return false;
    if (search && !c.name.toLowerCase().includes(search.toLowerCase()) && !c.city?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-black text-brand-navy">Clubs</h1>
            <p className="text-gray-500 text-sm mt-1">Join or manage sports clubs in your area.</p>
          </div>
          {user && (
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2.5 bg-brand-orange text-white text-sm font-bold rounded-lg hover:bg-lime/90 transition-colors" data-testid="create-club-btn">
              <Plus size={16} aria-hidden="true" /> Create Club
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#1A1F2E] border border-gray-200 rounded-xl p-1 mb-6 dark-section">
          {[{id:"mine",label:"My Clubs"},{id:"discover",label:"Discover"}].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${tab===t.id ? "bg-brand-orange text-white" : "text-gray-500 hover:text-white"}`}
              data-testid={`clubs-tab-${t.id}`}>
              {t.label} {t.id==="mine" && myClubs.length > 0 && `(${myClubs.length})`}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader size={28} className="text-brand-green animate-spin" /></div>
        ) : tab === "mine" ? (
          myClubs.length === 0 ? (
            <div className="text-center py-16" data-testid="no-clubs">
              <Trophy size={40} className="text-slate-600 mx-auto mb-3" />
              <p className="text-gray-500">You haven't joined any clubs yet.</p>
              <button onClick={() => setTab("discover")} className="mt-3 text-brand-green text-sm hover:underline">Browse clubs →</button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="my-clubs-list">
              {myClubs.map(c => <ClubCard key={c.id} club={c} isOwner={c.owner_id === user?.id} isMember={(c.members||[]).some(m=>m.user_id===user?.id)} />)}
            </div>
          )
        ) : (
          <div>
            <div className="flex gap-3 mb-6">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                <input type="text" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search clubs..."
                  className="w-full bg-[#1A1F2E] border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 dark-section" data-testid="discover-search" />
              </div>
              <select value={sportFilter} onChange={e=>setSportFilter(e.target.value)}
                className="bg-[#1A1F2E] border border-gray-200 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-green/50 dark-section" data-testid="sport-filter">
                <option value="">All Sports</option>
                {SPORTS.map(s=><option key={s} value={s}>{SPORT_LABELS[s]}</option>)}
              </select>
            </div>
            {discover.length === 0 ? (
              <div className="text-center py-12 text-gray-500 text-sm" data-testid="no-discover-clubs">No clubs found. Be the first to create one!</div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2" data-testid="discover-clubs-list">
                {discover.map(c => <ClubCard key={c.id} club={c} isOwner={false} isMember={false} />)}
              </div>
            )}
          </div>
        )}
      </div>
      {showCreate && <CreateClubModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />}
    </div>
  );
}
